import React, { useState } from 'react';
import {
  ListItem,
  Typography,
  Box,
  ButtonGroup,
  Button,
  Stack
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import NotesIcon from '@mui/icons-material/Notes';
import { ClockIcon } from '@mui/x-date-pickers';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContractDialog from './ContractDialog';
import { req } from '../utils';
import Chip from '@mui/material/Chip';
import NotesDialog from './NotesDialog';
import { ReminderDialog } from './ReminderDialog';
const colors = {
  primary: 'rgb(0, 164, 155)',
  primaryLight: '#3c8f7e',
  primaryDark: '#266358',
  secondary: '#314F71',
  success: '#4CAF50',
  distance: '#3182CE',
};

const VenueListItem = ({ venue, location, selected }) => {
    console.log(venue);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [openReminderDialog, setOpenReminderDialog] = useState(false);

  const handleContractClick = (e) => {
    e.stopPropagation();
    setOpenContractDialog(true);
  };

  const calculateDistance = (venueLat, venueLng) => {
    if (!location) return null;
    
    const R = 6371; // Earth's radius in km
    const dLat = (venueLat - location.lat) * Math.PI / 180;
    const dLon = (venueLng - location.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(location.lat * Math.PI / 180) * Math.cos(venueLat * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance.toFixed(1);
  };

  const distance = calculateDistance(
    venue.latitude,
    venue.longitude
  );

  const getContractStatus = (contractEnd) => {
    if (!contractEnd) return { text: "No Contract", color: "error" };
    
    const endDate = new Date(contractEnd * 1000);
    const now = new Date();
    const monthsUntilExpiry = (endDate - now) / (1000 * 60 * 60 * 24 * 30);
  
    if (monthsUntilExpiry < 0) {
      // Contract expired
      if (monthsUntilExpiry > -3) {
        return { text: "Recently Expired", color: "warning" };
      }
      return { text: "Contract Expired", color: "error" };
    } else if (monthsUntilExpiry < 3) {
      // Expiring soon
      return { text: "Expiring Soon", color: "warning" };
    }
    
    return { text: "Active Contract", color: "success" };
  };

  const contractStatus = getContractStatus(venue.contract_end);

  return (
    <ListItem 
      selected={selected}
      sx={{ 
        cursor: 'pointer',
        flexDirection: 'column',
        alignItems: 'stretch',
        px: 1,
        py: 2,
      }}
      divider
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 1.5
      }}>
        <Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: 1.5, 
            mb: 1
          }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 500,
                fontSize: '1.1rem',
                color: '#202124',
                flex: '1 1 auto',
                minWidth: 0,
                maxWidth: '100%'
              }}
            >
              <Box sx={{ 
                textOverflow: 'ellipsis', 
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textTransform: 'capitalize'
              }}>
                {venue.venue_name}
              </Box>
            </Typography>
            <Chip 
              label={contractStatus.text}
              color={contractStatus.color}
              size="small"
              sx={{ 
                height: '24px',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                flexShrink: 0,
                '& .MuiChip-label': {
                  px: 1,
                  py: 0.25,
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#5f6368',
              fontSize: '0.875rem'
            }}
          >
            {venue.address}
          </Typography>
        </Box>
      </Box>

      <Stack 
        direction="row" 
        spacing={1} 
        sx={{ 
          justifyContent: 'space-between',
          '& .MuiButtonGroup-root': {
            width: '100%',
            '& .MuiButton-root': {
              flex: 1,
              borderColor: 'rgba(0, 164, 155, 0.2)',
              '&:hover': {
                borderColor: colors.primaryLight,
                backgroundColor: 'rgba(0, 164, 155, 0.04)',
                color: colors.primaryLight
              }
            }
          }
        }}
      >
        <ButtonGroup 
          variant="text" 
          fullWidth
          sx={{
            '& .MuiButton-root': {
              borderColor: 'rgba(0, 164, 155, 0.2)',
              color: colors.primary,
              flexDirection: 'column',
              padding: '6px 4px',
              minHeight: '64px',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem',
                mb: 0.5,
                opacity: 0.9
              },
              '& span': {
                fontSize: '0.75rem',
                textTransform: 'none',
                fontWeight: 500,
                display: 'block',
                // Hide "& performance" on mobile
                '& .performance-text': {
                  display: {
                    xs: 'none',
                    sm: 'inline'
                  }
                }
              }
            }
          }}
        >
          <Button
            size="small"
            startIcon={null}
            onClick={handleContractClick}
          >
            <AssignmentIcon sx={{ mb: 0.5 }} />
            <span>
              Contract
              <span className="performance-text"> & performance</span>
            </span>
          </Button>
          <Button
            size="small"
            startIcon={null}
            onClick={(e) => {
              e.stopPropagation();
              setOpenNotesDialog(true);
            }}
          >
            <NotesIcon sx={{ mb: 0.5 }} />
            <span>Notes</span>
          </Button>
          <Button
            size="small"
            startIcon={null}
            onClick={(e) => {
              e.stopPropagation();
              setOpenReminderDialog(true);
            }}
          >
            <ClockIcon sx={{ mb: 0.5 }} />
            <span>Reminder</span>
          </Button>
          <Button
            size="small"
            startIcon={null}
            onClick={(e) => {
              e.stopPropagation();
              let mapUrl;
              
              // If we have coordinates, use them for precise location
              if (venue.latitude && venue.longitude) {
                mapUrl = `https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`;
              } else {
                // Otherwise use address and append Australia if not present
                let address = venue.address;
                if (!address.toLowerCase().includes('australia')) {
                  address += ', Australia';
                }
                mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
              }
              
              window.open(mapUrl, '_blank');
            }}
          >
            <MapIcon sx={{ mb: 0.5 }} />
            <span>{distance ? `${distance}km` : 'Map'}</span>
          </Button>
        </ButtonGroup>
      </Stack>
      
      <ContractDialog
        open={openContractDialog}
        onClose={() => setOpenContractDialog(false)}
        accountId={venue.account_id}
      />
      <NotesDialog
        open={openNotesDialog}
        onClose={() => setOpenNotesDialog(false)}
        accountId={venue.account_id}
        venueId={venue.venue_id}
      />
      <ReminderDialog
        open={openReminderDialog}
        onClose={() => setOpenReminderDialog(false)}
        businessName={venue.venue_name}
        accountId={venue.account_id}
        venueId={venue.venue_id}
        type="venue"
      />
    </ListItem>
  );
};

export default VenueListItem;
